import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Users, Car, UserCheck, Pencil, Save, X, Search, Loader2, MessageCircle, Send, Copy, Check, Share2, Trash2, Scissors, ArrowLeftRight } from "lucide-react";
import getApiUrl from "@/utils/api";
import { fmtChicagoDate, fmtChicagoTime, fmtChicagoDateTime } from "@/utils/timezone";
import { formatConfirmation, getConfirmation } from "@/utils/confirmation";
import { ConfirmAction } from "@/components/ConfirmAction";
import { CascadeAssignDialog } from "@/components/admin/CascadeAssignDialog";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";

const API = getApiUrl();

export function BookingDetailsDialog({ selectedBooking, drivers, getAuthHeaders, fetchData, onClose, editApiPrefix, onCreateReturnTrip }) {
  const apiPrefix = editApiPrefix || "/admin";
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [showMessages, setShowMessages] = useState(false);
  // Cascade confirmation state
  const [cascade, setCascade] = useState(null); // { driverId, driverName, available, blocked, rule }
  // Per-passenger payment state (must be before any early return)
  const [paxPayAmounts, setPaxPayAmounts] = useState({});
  const [paxPayLoading, setPaxPayLoading] = useState(null);
  // Split booking state
  const [showSplit, setShowSplit] = useState(false);
  const [splitSelected, setSplitSelected] = useState({});
  const [splitLoading, setSplitLoading] = useState(false);

  if (!selectedBooking) return null;

  const startEdit = () => {
    setForm({
      full_name: selectedBooking.full_name || "",
      whatsapp_number: selectedBooking.whatsapp_number || "",
      email: selectedBooking.email || "",
      flight_number: selectedBooking.flight_number || "",
      airline: selectedBooking.airline || "",
      arrival_terminal: selectedBooking.arrival_terminal || "",
      travel_date: selectedBooking.travel_date || "",
      travel_time: selectedBooking.travel_time || "",
      departure_time: selectedBooking.departure_time || "",
      pickup_location: selectedBooking.pickup_location || "",
      dropoff_location: selectedBooking.dropoff_location || "",
      housing_unit: selectedBooking.housing_unit || "",
      housing_address: selectedBooking.housing_address || "",
      num_passengers: selectedBooking.num_passengers || 1,
      num_suitcases: selectedBooking.num_suitcases || 0,
      total_suitcases: selectedBooking.total_suitcases ?? selectedBooking.num_suitcases ?? 0,
      total_carry_ons: selectedBooking.total_carry_ons ?? 0,
      total_barrels: selectedBooking.total_barrels ?? 0,
      passengers: Array.isArray(selectedBooking.passengers)
        ? selectedBooking.passengers.map(p => ({
            name: p?.name || "",
            whatsapp: p?.whatsapp || "",
            suitcases: Number.isInteger(p?.suitcases) ? p.suitcases : 0,
            carry_ons: Number.isInteger(p?.carry_ons) ? p.carry_ons : 0,
            amount_paid: parseFloat(p?.amount_paid) || 0,
            per_person_fare: parseFloat(p?.per_person_fare) || 0,
            balance_due: parseFloat(p?.balance_due) || 0,
          }))
        : [{ name: selectedBooking.full_name || "", whatsapp: selectedBooking.whatsapp_number || "", suitcases: 0, carry_ons: 0, amount_paid: 0, per_person_fare: 0, balance_due: 0 }],
      ride_type: selectedBooking.ride_type || "shared",
      trip_type: selectedBooking.trip_type || "arrival",
      trip_price: selectedBooking.trip_price || 0,
      luggage_fee: selectedBooking.luggage_fee || 0,
      tip_amount: selectedBooking.tip_amount || 0,
      price_locked: !!selectedBooking.price_locked,
      amount_paid: selectedBooking.amount_paid || 0,
      deposit_amount: selectedBooking.deposit_amount || 0,
      payment_method: selectedBooking.payment_method || "manual",
      payment_status: selectedBooking.payment_status || "pending",
      booking_status: selectedBooking.booking_status || "confirmed",
      notes: selectedBooking.notes || "",
      admin_notes: selectedBooking.admin_notes || "",
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!form.flight_number || form.flight_number.trim().length < 2) {
      toast.error("Flight number is required. Enter the flight number (e.g. UA123), not the confirmation code.");
      return;
    }
    try {
      // Detect whether the admin actually changed the price. If so, force
      // lock it so the auto-recalc engine doesn't override the admin's
      // manual override on the next /recalculate run. (To intentionally
      // unlock, admin uses the Unlock button while keeping the price the
      // same.)
      const originalPrice = Number(selectedBooking.trip_price || 0);
      const editedPrice = Number(form.trip_price || 0);
      const priceChanged = Math.abs(originalPrice - editedPrice) > 0.01;
      let payload = { ...form };
      if (priceChanged) {
        payload.price_locked = true;
      }
      await axios.put(API + apiPrefix + "/bookings/" + selectedBooking.id + "/edit", payload, getAuthHeaders());
      toast.success(payload.price_locked ? "Booking updated · price locked" : "Booking updated!");
      setEditing(false);
      fetchData();
    } catch (err) {
      toast.error("Failed to update booking");
    }
  };

  // Per-passenger payment recording
  const recordPassengerPayment = async (idx, method = "cash") => {
    const amt = parseFloat(paxPayAmounts[idx]);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setPaxPayLoading(idx);
    try {
      const res = await axios.post(
        `${API}${apiPrefix}/bookings/${selectedBooking.id}/passenger-payment`,
        { passenger_index: idx, amount: amt, method },
        getAuthHeaders()
      );
      toast.success(res.data.message);
      setPaxPayAmounts(prev => ({ ...prev, [idx]: "" }));
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Payment failed");
    } finally {
      setPaxPayLoading(null);
    }
  };

  // Message conversation
  const loadMessages = async () => {
    try {
      const res = await axios.get(`${API}/admin/bookings/${selectedBooking.id}/messages`, getAuthHeaders());
      setMessages(res.data);
      setShowMessages(true);
      // Mark as read
      await axios.put(`${API}/admin/messages/${selectedBooking.id}/mark-read`, {}, getAuthHeaders()).catch(() => {});
    } catch (e) {}
  };

  const sendMsg = async () => {
    if (!newMsg.trim()) return;
    try {
      await axios.post(`${API}/admin/bookings/${selectedBooking.id}/send-message`, { message: newMsg }, getAuthHeaders());
      toast.success("Message sent!");
      setNewMsg("");
      loadMessages();
    } catch (e) {
      toast.error("Failed to send");
    }
  };

  const handleUnassignDriver = async () => {
    try {
      await axios.put(API + "/admin/bookings/" + selectedBooking.id + "/unassign-driver", {}, getAuthHeaders());
      toast.success("Driver unassigned");
      fetchData();
      onClose();
    } catch (err) {
      toast.error("Failed to unassign driver");
    }
  };

  const handleAssignDriver = async (driverId, driverName) => {
    try {
      // Preview cascade — show confirmation prompt if there are matching passengers
      const preview = await axios.get(
        API + "/admin/bookings/" + selectedBooking.id + "/assign-driver/preview",
        getAuthHeaders()
      );
      const available = preview.data.available || [];
      const blocked = preview.data.skipped_with_other_driver || [];

      if (available.length === 0) {
        // No siblings — just assign the primary
        await doAssign(driverId, driverName, false);
        if (blocked.length > 0) {
          toast.info(`${blocked.length} other passenger${blocked.length > 1 ? "s" : ""} on this route already had a driver — left untouched.`);
        }
      } else {
        // Show cascade confirmation
        setCascade({
          driverId, driverName,
          available, blocked,
          rule: preview.data.rule,
        });
      }
    } catch (err) {
      toast.error("Failed to assign driver");
    }
  };

  const doAssign = async (driverId, driverName, cascadeFlag) => {
    try {
      const res = await axios.put(
        API + "/admin/bookings/" + selectedBooking.id + "/assign-driver?driver_id=" + driverId + "&cascade=" + (cascadeFlag ? "true" : "false"),
        {},
        getAuthHeaders()
      );
      const cascaded = res.data.cascaded || [];
      const skipped = res.data.skipped_with_other_driver || [];
      if (cascaded.length > 0) {
        toast.success(`Assigned ${driverName} to ${1 + cascaded.length} passengers`);
      } else {
        toast.success("Assigned to " + driverName);
      }
      if (skipped.length > 0) {
        toast.info(`${skipped.length} passenger${skipped.length > 1 ? "s" : ""} already had a different driver — skipped.`);
      }
      setCascade(null);
      fetchData();
      onClose();
    } catch (err) {
      toast.error("Failed to assign driver");
    }
  };

  const handleSplitBooking = async () => {
    const selectedIndices = Object.entries(splitSelected).filter(([, v]) => v).map(([k]) => parseInt(k));
    const allPax = selectedBooking.passengers || [];
    const splitAll = selectedIndices.length === 0 || selectedIndices.length === allPax.length;
    setSplitLoading(true);
    try {
      const payload = splitAll ? { passenger_indices: null } : { passenger_indices: selectedIndices };
      const res = await axios.post(`${API}${apiPrefix}/bookings/${selectedBooking.id}/split`, payload, getAuthHeaders());
      const newCount = (res.data.new_bookings || []).length;
      const names = (res.data.new_bookings || []).map(b => b.full_name).join(", ");
      toast.success(`Split into ${newCount} booking${newCount > 1 ? "s" : ""}: ${names}`);
      setShowSplit(false);
      setSplitSelected({});
      fetchData();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Split failed");
    } finally {
      setSplitLoading(false);
    }
  };

  const totalAmount = selectedBooking.total_amount || 0;
  const passengers = selectedBooking.passengers || [];
  const hasDriver = !!selectedBooking.assigned_driver_name;
  const confId = formatConfirmation(selectedBooking.id);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  return (
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <div className="flex items-center justify-between">
          <DialogTitle>Booking #{confId}</DialogTitle>
          <div className="flex gap-2">
            {editing ? (
              <>
                <ConfirmAction
                  title="Save Changes?"
                  description={`Save the edits to booking #${confId}?`}
                  onConfirm={saveEdit}
                  confirmText="Yes, Save"
                >
                  <Button size="sm" className="bg-green-600 hover:bg-green-700" data-testid="save-edit-btn">
                    <Save className="w-4 h-4 mr-1" /> Save
                  </Button>
                </ConfirmAction>
                <Button size="sm" variant="outline" onClick={() => setEditing(false)} data-testid="cancel-edit-btn">
                  <X className="w-4 h-4 mr-1" /> Cancel
                </Button>
              </>
            ) : (
              <>
                <Button size="sm" variant="outline" onClick={startEdit} data-testid="edit-booking-btn">
                  <Pencil className="w-4 h-4 mr-1" /> Edit
                </Button>
                <ConfirmAction
                  title="Delete Booking?"
                  description={`Permanently delete booking #${confId} for ${selectedBooking.full_name}? This cannot be undone.`}
                  onConfirm={async () => {
                    try {
                      await axios.delete(`${API}${apiPrefix}/bookings/${selectedBooking.id}`, getAuthHeaders());
                      toast.success("Booking deleted");
                      fetchData();
                      onClose();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Delete failed");
                    }
                  }}
                  confirmText="Yes, Delete"
                >
                  <Button size="sm" variant="destructive" data-testid="delete-booking-btn">
                    <Trash2 className="w-4 h-4 mr-1" /> Delete
                  </Button>
                </ConfirmAction>
                {(selectedBooking.passengers || []).length > 1 && (
                  <Button size="sm" variant="outline" className="text-orange-700 border-orange-300 hover:bg-orange-50" onClick={() => { setShowSplit(true); setSplitSelected({}); }} data-testid="split-booking-btn">
                    <Scissors className="w-4 h-4 mr-1" /> Split
                  </Button>
                )}
                {onCreateReturnTrip && (
                  <Button size="sm" variant="outline" className="text-blue-700 border-blue-300 hover:bg-blue-50" onClick={() => onCreateReturnTrip(selectedBooking)} data-testid="create-return-trip-btn">
                    <ArrowLeftRight className="w-4 h-4 mr-1" /> Create Return Trip
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </DialogHeader>

      <div className="space-y-6">
        {editing ? (
          <EditForm form={form} update={update} paxPayAmounts={paxPayAmounts} setPaxPayAmounts={setPaxPayAmounts} paxPayLoading={paxPayLoading} recordPassengerPayment={recordPassengerPayment} getAuthHeaders={getAuthHeaders} />
        ) : (
          <ViewDetails 
            booking={selectedBooking} 
            totalAmount={totalAmount}
            loadMessages={loadMessages}
            showMessages={showMessages}
            messages={messages}
            newMsg={newMsg}
            setNewMsg={setNewMsg}
            sendMsg={sendMsg}
            getAuthHeaders={getAuthHeaders}
          />
        )}

        <PassengersList passengers={passengers} />
        <DriverSection
          hasDriver={hasDriver}
          booking={selectedBooking}
          drivers={drivers}
          onUnassign={handleUnassignDriver}
          onAssign={handleAssignDriver}
        />

        {/* Cascade Assignment Confirmation Dialog (renders nothing when cascade is null) */}
        <CascadeAssignDialog
          cascade={cascade}
          selectedBooking={selectedBooking}
          onCancel={() => setCascade(null)}
          onAssign={(cascadeAll) => doAssign(cascade.driverId, cascade.driverName, cascadeAll)}
        />

        {/* Split Booking Panel */}
        {showSplit && (
          <SplitBookingPanel
            booking={selectedBooking}
            splitSelected={splitSelected}
            setSplitSelected={setSplitSelected}
            splitLoading={splitLoading}
            onSplit={handleSplitBooking}
            onCancel={() => { setShowSplit(false); setSplitSelected({}); }}
          />
        )}
      </div>
    </DialogContent>
  );
}

function EditForm({ form, update, paxPayAmounts, setPaxPayAmounts, paxPayLoading, recordPassengerPayment, getAuthHeaders }) {
  const [flightLoading, setFlightLoading] = useState(false);
  const [housingSuggestions, setHousingSuggestions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [housingOpen, setHousingOpen] = useState(false);
  const [pickupOpen, setPickupOpen] = useState(false);
  const [dropoffOpen, setDropoffOpen] = useState(false);

  // Load housing & location suggestions once
  useEffect(() => {
    if (getAuthHeaders) {
      axios.get(`${API}/admin/housing-suggestions`, getAuthHeaders())
        .then(r => {
          setHousingSuggestions(r.data?.housing_names || []);
          setLocationOptions(r.data?.locations || []);
        })
        .catch(() => {});
    }
  }, []);

  const lookupFlight = async () => {
    if (!form.flight_number || form.flight_number.length < 3) return;
    setFlightLoading(true);
    try {
      const dateParam = form.travel_date ? `?date=${form.travel_date}` : '';
      const res = await axios.get(`${API}/flight/lookup/${form.flight_number.toUpperCase()}${dateParam}`);
      if (res.data.found) {
        update("airline", res.data.airline || form.airline);
        const isReturn = form.trip_type === "return";
        // Auto-fill departure time for return trips
        if (isReturn && res.data.departure_time_prefill) {
          update("departure_time", res.data.departure_time_prefill);
          // Also set departure airport as pickup for return trips
          const depAirport = res.data.departure?.airport;
          const depIata = res.data.departure?.iata;
          if (depIata) {
            const airportMap = { ORD: "Chicago O'Hare International Airport (ORD)", MDW: "Chicago Midway International Airport (MDW)", MKE: "Milwaukee Mitchell International Airport (MKE)", MSN: "Dane County Regional Airport (MSN)" };
            const mapped = airportMap[depIata.toUpperCase()];
            if (mapped && !form.dropoff_location) update("dropoff_location", mapped);
          }
        }
        // Auto-fill arrival time for arrival trips
        if (!isReturn && res.data.arrival_time_prefill) {
          update("travel_time", res.data.arrival_time_prefill);
        }
        // Build toast
        const parts = [res.data.airline];
        if (isReturn) {
          if (res.data.departure?.terminal) parts.push(`Departs Terminal ${res.data.departure.terminal}`);
          if (res.data.departure_time_prefill) parts.push(`Departs ${res.data.departure_time_prefill}`);
        } else {
          if (res.data.arrival?.terminal) parts.push(`Terminal ${res.data.arrival.terminal}`);
          if (res.data.arrival_time_prefill) parts.push(`Arrives ${res.data.arrival_time_prefill}`);
        }
        toast.success(parts.join(' | '));
        if (res.data.route_warning) toast.warning(res.data.route_warning, { duration: 8000 });
      } else {
        toast.info("Flight not found in live data");
      }
    } catch (e) { toast.error("Flight lookup failed"); }
    setFlightLoading(false);
  };

  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label className="text-xs">Full Name</Label>
        <Input value={form.full_name} onChange={e => update("full_name", e.target.value)} data-testid="edit-full-name" />
      </div>
      <div>
        <Label className="text-xs">WhatsApp</Label>
        <Input value={form.whatsapp_number} onChange={e => update("whatsapp_number", e.target.value)} data-testid="edit-whatsapp" />
      </div>
      <div>
        <Label className="text-xs">Email</Label>
        <Input value={form.email} onChange={e => update("email", e.target.value)} data-testid="edit-email" />
      </div>
      <div>
        <Label className="text-xs">Flight Number <span className="text-red-500">*</span></Label>
        <div className="flex gap-1">
          <Input value={form.flight_number} onChange={e => update("flight_number", e.target.value)} data-testid="edit-flight" className={`flex-1 ${!form.flight_number ? 'border-red-300 bg-red-50/50' : ''}`} placeholder="e.g. UA123, AA456" required />
          <Button
            type="button" variant="outline" size="sm"
            className="h-9 px-2 text-blue-600 border-blue-200 hover:bg-blue-50"
            disabled={flightLoading || !form.flight_number}
            onClick={lookupFlight}
            data-testid="edit-flight-lookup-btn"
          >
            {flightLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
          </Button>
        </div>
        <p className="text-[10px] text-amber-600 mt-0.5 font-medium">Enter the flight number (e.g. UA123), NOT the airline confirmation/booking code (e.g. ABCDEF)</p>
      </div>
      <div>
        <Label className="text-xs">Airline</Label>
        <Input value={form.airline} onChange={e => update("airline", e.target.value)} data-testid="edit-airline" />
      </div>
      <div>
        <Label className="text-xs">Terminal</Label>
        <Input value={form.arrival_terminal} onChange={e => update("arrival_terminal", e.target.value)} placeholder="e.g., 3, 5, T1" data-testid="edit-terminal" />
      </div>
      <div>
        <Label className="text-xs">Trip Type</Label>
        <select className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" value={form.trip_type || "arrival"} onChange={e => update("trip_type", e.target.value)} data-testid="edit-trip-type">
          <option value="arrival">Arrival (Airport → Housing)</option>
          <option value="return">Return (Housing → Airport)</option>
        </select>
      </div>
      <div>
        <Label className="text-xs">Ride Type</Label>
        <select className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" value={form.ride_type || "shared"} onChange={e => update("ride_type", e.target.value)} data-testid="edit-ride-type">
          <option value="shared">Shared</option>
          <option value="private">Private</option>
          <option value="no_deposit">Solo (no-deposit rate)</option>
        </select>
      </div>
      <div>
        <Label className="text-xs">Travel Date</Label>
        <Input type="date" value={form.travel_date} onChange={e => update("travel_date", e.target.value)} data-testid="edit-date" />
      </div>
      <div>
        <Label className="text-xs">Travel Time</Label>
        <Input value={form.travel_time} onChange={e => update("travel_time", e.target.value)} data-testid="edit-time" />
      </div>
      {form.trip_type === "return" && (
        <div>
          <Label className="text-xs">Flight Departure Time</Label>
          <Input value={form.departure_time || ""} onChange={e => update("departure_time", e.target.value)} placeholder="HH:MM (auto-filled from flight)" data-testid="edit-departure-time" />
        </div>
      )}
      <div>
        <Label className="text-xs">Pickup Location</Label>
        <div className="relative">
          <Input
            value={form.pickup_location || ""}
            onChange={e => { update("pickup_location", e.target.value); setPickupOpen(true); }}
            onFocus={() => setPickupOpen(true)}
            onBlur={() => setTimeout(() => setPickupOpen(false), 200)}
            placeholder="Airport or housing name"
            data-testid="edit-pickup"
            autoComplete="off"
          />
          {pickupOpen && locationOptions.length > 0 && (
            <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
              {locationOptions
                .filter(s => !form.pickup_location || s.toLowerCase().includes((form.pickup_location || "").toLowerCase()))
                .slice(0, 12)
                .map(s => (
                  <button key={s} type="button" className="w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 hover:text-blue-700 transition-colors"
                    onMouseDown={e => { e.preventDefault(); update("pickup_location", s); setPickupOpen(false); }}>
                    {s}
                  </button>
                ))}
            </div>
          )}
        </div>
      </div>
      <div>
        <Label className="text-xs">Dropoff Location</Label>
        <div className="relative">
          <Input
            value={form.dropoff_location || ""}
            onChange={e => { update("dropoff_location", e.target.value); setDropoffOpen(true); }}
            onFocus={() => setDropoffOpen(true)}
            onBlur={() => setTimeout(() => setDropoffOpen(false), 200)}
            placeholder="Airport or housing name"
            data-testid="edit-dropoff"
            autoComplete="off"
          />
          {dropoffOpen && locationOptions.length > 0 && (
            <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
              {locationOptions
                .filter(s => !form.dropoff_location || s.toLowerCase().includes((form.dropoff_location || "").toLowerCase()))
                .slice(0, 12)
                .map(s => (
                  <button key={s} type="button" className="w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 hover:text-blue-700 transition-colors"
                    onMouseDown={e => { e.preventDefault(); update("dropoff_location", s); setDropoffOpen(false); }}>
                    {s}
                  </button>
                ))}
            </div>
          )}
        </div>
      </div>
      <div>
        <Label className="text-xs">Housing Unit</Label>
        <div className="relative">
          <Input
            value={form.housing_unit}
            onChange={e => { update("housing_unit", e.target.value); setHousingOpen(true); }}
            onFocus={() => setHousingOpen(true)}
            onBlur={() => setTimeout(() => setHousingOpen(false), 200)}
            placeholder="e.g., IRH, Bunker Run"
            data-testid="edit-housing-unit"
            autoComplete="off"
          />
          {housingOpen && housingSuggestions.length > 0 && (
            <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
              {housingSuggestions
                .filter(s => !form.housing_unit || s.toLowerCase().includes((form.housing_unit || "").toLowerCase()))
                .slice(0, 10)
                .map(s => (
                  <button
                    key={s}
                    type="button"
                    className="w-full text-left px-3 py-1.5 text-sm hover:bg-green-50 hover:text-green-700 transition-colors"
                    onMouseDown={(e) => { e.preventDefault(); update("housing_unit", s); setHousingOpen(false); }}
                    data-testid={`housing-suggestion-${s.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    {s}
                  </button>
                ))}
            </div>
          )}
        </div>
      </div>
      <div>
        <Label className="text-xs">Housing Address</Label>
        <AddressAutocomplete value={form.housing_address || ""} onChange={(v) => update("housing_address", v)} placeholder="Housing address" dataTestId="edit-housing" />
      </div>
      <div>
        <Label className="text-xs">Passengers</Label>
        <Input type="number" min="1" value={form.num_passengers} onChange={e => update("num_passengers", parseInt(e.target.value) || 1)} data-testid="edit-passengers" />
      </div>
      <div>
        <Label className="text-xs">Suitcases (total)</Label>
        <Input type="number" min="0" value={form.total_suitcases} onChange={e => update("total_suitcases", parseInt(e.target.value) || 0)} data-testid="edit-suitcases" />
      </div>
      <div>
        <Label className="text-xs flex items-center justify-between">
          <span>Trip Price ($)</span>
          {form.price_locked && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">
              🔒 LOCKED
            </span>
          )}
        </Label>
        <Input type="number" step="0.01" value={form.trip_price} onChange={e => update("trip_price", parseFloat(e.target.value) || 0)} data-testid="edit-price" />
        {form.price_locked ? (
          <p className="text-[10px] text-amber-700 mt-1">
            Auto-recalc won&apos;t touch this booking.{" "}
            <button
              type="button"
              onClick={() => update("price_locked", false)}
              className="underline text-blue-700 hover:text-blue-900"
              data-testid="unlock-price-btn"
            >
              Unlock (let recalc manage it)
            </button>
          </p>
        ) : (
          <p className="text-[10px] text-gray-500 mt-1">
            Changing this price will automatically lock it from auto-recalc.
          </p>
        )}
      </div>
      <div>
        <Label className="text-xs">Amount Paid ($)</Label>
        <Input type="number" step="0.01" value={form.amount_paid} onChange={e => update("amount_paid", parseFloat(e.target.value) || 0)} data-testid="edit-amount-paid" />
      </div>
      <div>
        <Label className="text-xs">Deposit Amount ($)</Label>
        <Input type="number" step="0.01" value={form.deposit_amount} onChange={e => update("deposit_amount", parseFloat(e.target.value) || 0)} data-testid="edit-deposit" />
      </div>
      <div>
        <Label className="text-xs">Luggage Fee ($)</Label>
        <Input type="number" step="0.01" value={form.luggage_fee} onChange={e => update("luggage_fee", parseFloat(e.target.value) || 0)} data-testid="edit-luggage-fee" />
      </div>
      <div>
        <Label className="text-xs">Driver Tip ($)</Label>
        <Input type="number" step="0.01" value={form.tip_amount} onChange={e => update("tip_amount", parseFloat(e.target.value) || 0)} data-testid="edit-tip" />
      </div>
      <div>
        <Label className="text-xs">Payment Method</Label>
        <select className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" value={form.payment_method || ""} onChange={e => update("payment_method", e.target.value)} data-testid="edit-payment-method">
          <option value="manual">Manual</option>
          <option value="stripe">Stripe</option>
          <option value="cash">Cash</option>
          <option value="zelle">Zelle</option>
          <option value="cashapp">CashApp</option>
          <option value="bank_transfer">Bank Transfer</option>
        </select>
      </div>
      <div>
        <Label className="text-xs">Payment Status</Label>
        <select className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" value={form.payment_status || "pending"} onChange={e => update("payment_status", e.target.value)} data-testid="edit-payment-status">
          <option value="pending">Pending</option>
          <option value="paid">Paid</option>
          <option value="deposit_paid">Deposit Paid</option>
          <option value="refunded">Refunded</option>
          <option value="failed">Failed</option>
        </select>
      </div>
      <div>
        <Label className="text-xs">Booking Status</Label>
        <select className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" value={form.booking_status || "confirmed"} onChange={e => update("booking_status", e.target.value)} data-testid="edit-booking-status">
          <option value="confirmed">Confirmed</option>
          <option value="pending">Pending</option>
          <option value="cancelled">Cancelled</option>
          <option value="completed">Completed</option>
        </select>
      </div>
      <div className="col-span-2">
        <Label className="text-xs">Customer Notes</Label>
        <Input value={form.notes} onChange={e => update("notes", e.target.value)} placeholder="Customer-visible notes…" data-testid="edit-notes" />
      </div>
      <div className="col-span-1">
        <Label className="text-xs">Total Suitcases (1st free · $5 each additional)</Label>
        <Input type="number" min="0" max="30" value={form.total_suitcases ?? 0} onChange={e => update("total_suitcases", parseInt(e.target.value) || 0)} data-testid="edit-total-suitcases" />
      </div>
      <div className="col-span-1">
        <Label className="text-xs">Total Carry-ons (1st free)</Label>
        <Input type="number" min="0" max="20" value={form.total_carry_ons || 0} onChange={e => update("total_carry_ons", parseInt(e.target.value) || 0)} data-testid="edit-total-carry-ons" />
      </div>
      <div className="col-span-1">
        <Label className="text-xs">Total Barrels</Label>
        <Input type="number" min="0" max="20" value={form.total_barrels || 0} onChange={e => update("total_barrels", parseInt(e.target.value) || 0)} data-testid="edit-total-barrels" />
      </div>
      <div className="col-span-2 space-y-2 border-t pt-3">
        <Label className="text-xs font-bold uppercase text-emerald-700">Passengers ({(form.passengers || []).length})</Label>
        {(form.passengers || []).map((p, i) => (
          <div key={i} className="border rounded-lg p-2 space-y-2 bg-slate-50" data-testid={`edit-passenger-row-${i}`}>
            <div className="grid grid-cols-3 gap-2">
              <Input
                value={p.name || ""}
                onChange={e => update("passengers", (form.passengers || []).map((pp, j) => j === i ? { ...pp, name: e.target.value } : pp))}
                placeholder={`Passenger ${i + 1} name`}
                data-testid={`edit-passenger-${i}-name`}
              />
              <Input
                value={p.whatsapp || ""}
                onChange={e => update("passengers", (form.passengers || []).map((pp, j) => j === i ? { ...pp, whatsapp: e.target.value } : pp))}
                placeholder="WhatsApp (+1234...)"
                data-testid={`edit-passenger-${i}-whatsapp`}
              />
              <select
                value={housingSuggestions.includes(p.housing_unit) ? p.housing_unit : (p.housing_unit ? "__custom__" : "")}
                onChange={e => {
                  const val = e.target.value;
                  if (val === "__custom__") return;
                  update("passengers", (form.passengers || []).map((pp, j) => j === i ? { ...pp, housing_unit: val } : pp));
                }}
                className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                data-testid={`edit-passenger-${i}-housing`}
              >
                <option value="">Housing...</option>
                {housingSuggestions.map(h => <option key={h} value={h}>{h}</option>)}
              </select>
            </div>
            {(form.num_passengers > 1) && (
              <div className="grid grid-cols-2 gap-2">
                <select
                  value={p.pickup_location || ""}
                  onChange={e => update("passengers", (form.passengers || []).map((pp, j) => j === i ? { ...pp, pickup_location: e.target.value } : pp))}
                  className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                  data-testid={`edit-passenger-${i}-pickup`}
                >
                  <option value="">Pickup (same as booking)</option>
                  {locationOptions.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
                <select
                  value={p.dropoff_location || ""}
                  onChange={e => update("passengers", (form.passengers || []).map((pp, j) => j === i ? { ...pp, dropoff_location: e.target.value } : pp))}
                  className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                  data-testid={`edit-passenger-${i}-dropoff`}
                >
                  <option value="">Dropoff (same as booking)</option>
                  {locationOptions.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
            )}
            <div className="grid grid-cols-4 gap-2">
              <div>
                <Label className="text-[10px] text-muted-foreground">Suitcases</Label>
                <Input
                  type="number" min="0" max="20"
                  value={p.suitcases ?? 0}
                  onChange={e => update("passengers", (form.passengers || []).map((pp, j) => j === i ? { ...pp, suitcases: parseInt(e.target.value) || 0 } : pp))}
                  placeholder="0"
                  data-testid={`edit-passenger-${i}-suitcases`}
                  className="h-8"
                />
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground">Carry-ons</Label>
                <Input
                  type="number" min="0" max="10"
                  value={p.carry_ons ?? 0}
                  onChange={e => update("passengers", (form.passengers || []).map((pp, j) => j === i ? { ...pp, carry_ons: parseInt(e.target.value) || 0 } : pp))}
                  placeholder="0"
                  data-testid={`edit-passenger-${i}-carry-ons`}
                  className="h-8"
                />
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground">Paid</Label>
                <div className="text-sm font-bold text-emerald-700 h-8 flex items-center" data-testid={`edit-passenger-${i}-paid`}>
                  ${(p.amount_paid || 0).toFixed(2)}
                </div>
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground">Balance</Label>
                <div className={`text-sm font-bold h-8 flex items-center ${(p.balance_due || 0) > 0 ? "text-rose-600" : "text-emerald-600"}`} data-testid={`edit-passenger-${i}-balance`}>
                  {(p.balance_due || 0) > 0 ? `$${(p.balance_due || 0).toFixed(2)}` : "✓ Paid"}
                </div>
              </div>
            </div>
            {/* Per-passenger payment input */}
            {(p.balance_due || 0) > 0.01 && (
              <div className="flex items-center gap-2 bg-emerald-50 rounded p-1.5" data-testid={`passenger-${i}-payment-row`}>
                <span className="text-[10px] text-emerald-700 font-bold whitespace-nowrap">💰 Record payment:</span>
                <Input
                  type="number" min="0" step="0.01"
                  value={paxPayAmounts[i] || ""}
                  onChange={e => setPaxPayAmounts(prev => ({ ...prev, [i]: e.target.value }))}
                  placeholder={`$${(p.balance_due || 0).toFixed(2)}`}
                  className="h-7 w-24 text-xs"
                  data-testid={`passenger-${i}-payment-amount`}
                />
                <Button
                  type="button" size="sm" variant="default"
                  className="h-7 text-[10px] bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => recordPassengerPayment(i, "cash")}
                  disabled={paxPayLoading === i}
                  data-testid={`passenger-${i}-pay-cash-btn`}
                >
                  {paxPayLoading === i ? "..." : "Cash"}
                </Button>
                <Button
                  type="button" size="sm" variant="outline"
                  className="h-7 text-[10px]"
                  onClick={() => recordPassengerPayment(i, "zelle")}
                  disabled={paxPayLoading === i}
                  data-testid={`passenger-${i}-pay-zelle-btn`}
                >
                  Zelle
                </Button>
              </div>
            )}
          </div>
        ))}
        <div className="flex gap-2">
          <Button
            type="button" variant="outline" size="sm"
            onClick={() => update("passengers", [...(form.passengers || []), { name: "", whatsapp: "", suitcases: 0, carry_ons: 0 }])}
            data-testid="edit-add-passenger-btn"
          >+ Add passenger</Button>
          {(form.passengers || []).length > 1 && (
            <Button
              type="button" variant="outline" size="sm"
              onClick={() => update("passengers", (form.passengers || []).slice(0, -1))}
              data-testid="edit-remove-passenger-btn"
            >− Remove last</Button>
          )}
        </div>
        <p className="text-[10px] text-muted-foreground">
          Tip: per-passenger counts are for driver planning. Booking-level totals above still determine the luggage fee.
        </p>
      </div>
      <div className="col-span-2">
        <Label className="text-xs">Admin Notes (internal)</Label>
        <Input value={form.admin_notes} onChange={e => update("admin_notes", e.target.value)} placeholder="Internal notes (not visible to customer)…" data-testid="edit-admin-notes" />
      </div>
    </div>
  );
}

function ViewDetails({ booking, totalAmount, loadMessages, showMessages, messages, newMsg, setNewMsg, sendMsg, getAuthHeaders }) {
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const confirmationNo = getConfirmation(booking);

  const copyConf = async () => {
    if (!confirmationNo) return;
    const name = (booking.full_name || "").split(" ")[0] || "there";
    const dest = booking.dropoff_location || booking.housing_unit || "TBD";
    const addr = booking.housing_address ? `\n   📍 ${booking.housing_address}` : "";
    const flight = booking.flight_number ? `${booking.airline ? booking.airline + " " : ""}${booking.flight_number}` : "TBD";
    const term = booking.arrival_terminal ? ` (Terminal ${booking.arrival_terminal})` : "";
    const tripPrice = Number(booking.trip_price || 0).toFixed(0);
    const luggage = Number(booking.luggage_fee || 0);
    const tip = Number(booking.tip_amount || 0);
    const paid = Number(booking.amount_paid || 0).toFixed(0);
    const total = (booking.trip_price || 0) + (booking.luggage_fee || 0) + (booking.tip_amount || 0);
    const balance = Math.max(0, total - (booking.amount_paid || 0)).toFixed(0);
    const pax = booking.num_passengers || 1;
    const allPassengers = (booking.passengers && booking.passengers.length > 1)
      ? `\n👥 Group: ${booking.passengers.map(p => p.name).filter(Boolean).join(", ")}`
      : "";
    const dateWithDay = (() => {
      if (!booking.travel_date) return "TBD";
      const d = new Date(booking.travel_date + "T12:00:00");
      if (isNaN(d)) return booking.travel_date;
      const wd = d.toLocaleDateString('en-US', { weekday: 'long' });
      return `${wd}, ${booking.travel_date}`;
    })();

    const text = `═══════════════════════════════
🚐 EASY TRANSPORTATION J1
   Chicago Airports ↔ Wisconsin
═══════════════════════════════

Hi ${name}!

Your Confirmation Number is:
🎟️  ${confirmationNo}

📅 Date: ${dateWithDay}
🕑 Time: ${booking.travel_time || "TBD"}
✈️  Flight: ${flight}${term}

🛬 PICKUP: ${booking.pickup_location || "TBD"}
🏠 DROP-OFF: ${dest}${addr}

👥 Passengers: ${pax}${allPassengers}

─── Payment ───
💰 Trip Price:  $${tripPrice}${luggage > 0 ? `\n🧳 Luggage:     $${luggage.toFixed(0)}` : ""}${tip > 0 ? `\n❤️  Driver Tip:  $${tip.toFixed(0)}` : ""}
💳 Paid:        $${paid}
💵 Balance:     $${balance} (due at pickup)

───────────────────────────────
📞 (815) 914-8139
🌐 easytransportationj1.com
📧 Miketransportsj1@gmail.com
───────────────────────────────`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Copied ${confirmationNo} + full booking details`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="grid grid-cols-2 gap-4 text-sm">
      <div className="col-span-2 flex items-center gap-2">
        <p className="text-muted-foreground text-xs">Confirmation #</p>
        <button
          type="button"
          onClick={copyConf}
          className="inline-flex items-center gap-1.5 px-3 py-1 font-mono font-bold text-sm rounded-lg border-2 border-amber-400 bg-gradient-to-r from-yellow-100 to-amber-100 text-amber-900 hover:from-yellow-200 hover:to-amber-200 transition-colors shadow-sm"
          data-testid="details-copy-confirmation"
          title="Click to copy"
        >
          {copied ? <Check className="w-4 h-4 text-green-700" /> : <Copy className="w-4 h-4" />}
          #{confirmationNo}
        </button>
        <button
          type="button"
          onClick={async () => {
            if (sending) return;
            setSending(true);
            try {
              const res = await axios.post(`${API}/admin/bookings/${booking.id}/send-confirmation`, {}, { headers: { ...(typeof getAuthHeaders === "function" ? getAuthHeaders() : {}), "Content-Type": "application/json" } });
              toast.success(res.data?.message || "Confirmation sent");
            } catch (e) {
              toast.error("Failed to send confirmation");
            } finally {
              setSending(false);
            }
          }}
          disabled={sending}
          className="inline-flex items-center gap-1.5 px-3 py-1 font-bold text-sm rounded-lg border-2 border-blue-400 bg-gradient-to-r from-blue-50 to-cyan-50 text-blue-800 hover:from-blue-100 hover:to-cyan-100 transition-colors shadow-sm disabled:opacity-50"
          data-testid="details-send-info"
          title="Send full booking info via WhatsApp + email (all passengers)"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
          Send Info
        </button>
      </div>
      <div>
        <p className="text-muted-foreground">Main Contact</p>
        <p className="font-semibold">{booking.full_name}</p>
      </div>
      <div>
        <p className="text-muted-foreground">WhatsApp</p>
        <p className="font-semibold text-green-600">{booking.whatsapp_number}</p>
      </div>
      <div>
        <p className="text-muted-foreground">Flight</p>
        <p className="font-semibold">{booking.airline} - {booking.flight_number}</p>
      </div>
      <div>
        <p className="text-muted-foreground">Terminal</p>
        <p className="font-semibold">{booking.arrival_terminal || "Not specified"}</p>
      </div>
      <div>
        <p className="text-muted-foreground">Date and Time</p>
        <p className="font-semibold">{(() => {
          const d = booking.travel_date ? new Date(booking.travel_date + "T12:00:00") : null;
          if (!d || isNaN(d)) return `${booking.travel_date} at ${booking.travel_time}`;
          const wd = d.toLocaleDateString('en-US', { weekday: 'long' });
          return `${wd}, ${booking.travel_date} at ${booking.travel_time}`;
        })()}</p>
      </div>
      <div>
        <p className="text-muted-foreground">Housing</p>
        <p className="font-semibold">{booking.housing_unit || booking.dropoff_location}</p>
        {booking.housing_address && booking.housing_address !== booking.housing_unit && (
          <p className="text-sm text-blue-600">{booking.housing_address}</p>
        )}
      </div>
      <div>
        <p className="text-muted-foreground">Payment Method</p>
        <p className="font-semibold capitalize">{booking.payment_method}</p>
      </div>
      <div>
        <p className="text-muted-foreground">Total</p>
        <p className="font-bold text-primary">${totalAmount.toFixed(2)}</p>
      </div>
      {booking.created_at && (
        <div>
          <p className="text-muted-foreground">Booked On</p>
          <p className="font-semibold text-sm">{fmtChicagoDate(booking.created_at)} {fmtChicagoTime(booking.created_at)} CT</p>
        </div>
      )}
      {booking.notes && (
        <div className="col-span-2">
          <p className="text-muted-foreground">Notes</p>
          <p className="font-semibold">{booking.notes}</p>
        </div>
      )}

      {/* Per-Passenger Payment Breakdown */}
      <div className="col-span-2 bg-gray-50 rounded-xl p-4 mt-2" data-testid="payment-breakdown-details">
        <p className="font-semibold text-gray-700 mb-3 text-sm">Payment Breakdown</p>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Trip Price</span>
            <span className="font-medium">${(booking.trip_price || 0).toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Deposit Paid</span>
            <span className="font-medium text-green-700">${(booking.deposit_amount || 0).toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Total Paid</span>
            <span className="font-semibold text-green-700">${(booking.amount_paid || 0).toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Balance Due</span>
            <span className={`font-bold ${(booking.balance_due || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>${(booking.balance_due || 0).toFixed(2)}</span>
          </div>
        </div>
        {(booking.num_passengers || 1) > 1 && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <p className="text-xs font-semibold text-gray-500 mb-2">PER PERSON ({booking.num_passengers} passengers)</p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Price/person</span>
                <span className="font-medium">${(booking.per_person_price || (booking.trip_price / booking.num_passengers)).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Deposit/person</span>
                <span className="font-medium text-green-700">${(booking.per_person_deposit || 20).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Paid/person</span>
                <span className="font-semibold text-green-700">${((booking.amount_paid || 0) / booking.num_passengers).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Balance/person</span>
                <span className={`font-bold ${(booking.balance_due || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>${(booking.per_person_balance || ((booking.balance_due || 0) / booking.num_passengers)).toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Message Conversation */}
      <div className="col-span-2 border-t pt-4 mt-2">
        <Button variant="outline" size="sm" onClick={loadMessages} className="w-full gap-2 text-green-700 border-green-200 hover:bg-green-50" data-testid="show-messages-btn">
          <MessageCircle className="w-4 h-4" />
          {showMessages ? "Refresh Messages" : "View Conversation"}
          {messages.filter(m => m.direction === "incoming" && !m.read).length > 0 && (
            <Badge className="bg-red-500 text-white text-[10px] ml-1">{messages.filter(m => m.direction === "incoming" && !m.read).length} new</Badge>
          )}
        </Button>
        {showMessages && (
          <div className="mt-3 space-y-2 max-h-64 overflow-y-auto" data-testid="messages-list">
            {messages.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">No messages yet</p>
            ) : (
              [...messages].reverse().map((msg, i) => (
                <div key={i} className={`p-2.5 rounded-lg text-sm ${msg.direction === "incoming" ? "bg-blue-50 border border-blue-100 mr-8" : "bg-green-50 border border-green-100 ml-8"}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-[10px] font-semibold ${msg.direction === "incoming" ? "text-blue-600" : "text-green-600"}`}>
                      {msg.direction === "incoming" ? booking.full_name : "Admin"}
                    </span>
                    <span className="text-[9px] text-muted-foreground">
                      {msg.sent_at ? fmtChicagoDateTime(msg.sent_at) : ''}
                    </span>
                  </div>
                  <p className="text-xs">{msg.message}</p>
                </div>
              ))
            )}
            <div className="flex gap-2 mt-2">
              <Input
                value={newMsg}
                onChange={(e) => setNewMsg(e.target.value)}
                placeholder="Type a message..."
                className="h-9 text-sm"
                onKeyDown={(e) => e.key === "Enter" && sendMsg()}
                data-testid="inline-message-input"
              />
              <Button size="sm" className="h-9 bg-green-600 hover:bg-green-700 px-3" onClick={sendMsg} disabled={!newMsg.trim()} data-testid="inline-send-btn">
                <Send className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SplitBookingPanel({ booking, splitSelected, setSplitSelected, splitLoading, onSplit, onCancel }) {
  const passengers = booking.passengers || [];
  const selectedCount = Object.values(splitSelected).filter(Boolean).length;
  const allSelected = selectedCount === passengers.length;
  const noneSelected = selectedCount === 0;
  const splitAll = noneSelected || allSelected;
  const perPersonPrice = passengers.length > 0 ? (booking.trip_price || 0) / passengers.length : 0;

  return (
    <div className="border-2 border-orange-300 rounded-xl bg-gradient-to-br from-orange-50 to-amber-50 p-4 space-y-3" data-testid="split-booking-panel">
      <div className="flex items-center justify-between">
        <h4 className="font-bold text-orange-900 flex items-center gap-2">
          <Scissors className="w-5 h-5" />
          Split Group Booking
        </h4>
        <Button size="sm" variant="ghost" onClick={onCancel} data-testid="split-cancel-btn">
          <X className="w-4 h-4" />
        </Button>
      </div>
      <p className="text-xs text-orange-800">
        Select passengers to split into individual bookings, or leave all unchecked to split everyone.
      </p>
      <div className="space-y-2">
        {passengers.map((p, idx) => {
          const paxPickup = (p.pickup_location || "").trim();
          const paxDropoff = (p.dropoff_location || "").trim();
          const paxHousing = (p.housing_unit || "").trim();
          return (
            <label key={idx} className={`flex items-center gap-3 p-2.5 rounded-lg border-2 cursor-pointer transition-all ${splitSelected[idx] ? "bg-orange-100 border-orange-400" : "bg-white border-gray-200 hover:border-orange-200"}`} data-testid={`split-passenger-${idx}`}>
              <input
                type="checkbox"
                checked={!!splitSelected[idx]}
                onChange={e => setSplitSelected(prev => ({ ...prev, [idx]: e.target.checked }))}
                className="w-4 h-4 accent-orange-600"
                data-testid={`split-checkbox-${idx}`}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm">{p.name || `Passenger ${idx + 1}`}</span>
                  {idx === 0 && <Badge variant="outline" className="text-[9px] py-0">Main</Badge>}
                </div>
                <div className="text-[10px] text-gray-500 mt-0.5 flex flex-wrap gap-x-3">
                  {paxPickup && <span>Pickup: {paxPickup.length > 30 ? paxPickup.slice(0, 30) + "..." : paxPickup}</span>}
                  {paxDropoff && <span>Dropoff: {paxDropoff.length > 30 ? paxDropoff.slice(0, 30) + "..." : paxDropoff}</span>}
                  {paxHousing && <span>Housing: {paxHousing}</span>}
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-xs font-bold text-gray-700">${perPersonPrice.toFixed(2)}</div>
                <div className="text-[10px] text-emerald-600">Paid: ${(p.amount_paid || 0).toFixed(2)}</div>
              </div>
            </label>
          );
        })}
      </div>
      <div className="flex items-center justify-between pt-2 border-t border-orange-200">
        <div className="text-xs text-orange-800">
          {splitAll
            ? <span className="font-semibold">All {passengers.length} passengers will become individual bookings</span>
            : <span><span className="font-semibold">{selectedCount}</span> passenger{selectedCount > 1 ? "s" : ""} will be split off. <span className="font-semibold">{passengers.length - selectedCount}</span> remain in original.</span>
          }
        </div>
        <ConfirmAction
          title="Split Booking?"
          description={splitAll
            ? `This will split all ${passengers.length} passengers into separate individual bookings and remove the original group booking. This cannot be undone.`
            : `This will split ${selectedCount} selected passenger${selectedCount > 1 ? "s" : ""} into individual booking${selectedCount > 1 ? "s" : ""}. The original booking will keep the remaining ${passengers.length - selectedCount} passenger${passengers.length - selectedCount > 1 ? "s" : ""}.`
          }
          onConfirm={onSplit}
          confirmText="Yes, Split"
        >
          <Button
            size="sm"
            className="bg-orange-600 hover:bg-orange-700 text-white"
            disabled={splitLoading}
            data-testid="split-confirm-btn"
          >
            {splitLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Scissors className="w-4 h-4 mr-1" />}
            {splitAll ? "Split All" : `Split ${selectedCount}`}
          </Button>
        </ConfirmAction>
      </div>
    </div>
  );
}

function PassengersList({ passengers }) {
  if (!passengers || passengers.length === 0) return null;
  const isGroup = passengers.length > 1;
  return (
    <div className={`border-t pt-4 ${isGroup ? "bg-gradient-to-br from-purple-50 via-fuchsia-50 to-pink-50 -mx-6 px-6 py-5 border-y-4 border-purple-400 rounded-lg shadow-md" : ""}`}>
      <h4 className={`font-bold mb-3 flex items-center gap-2 ${isGroup ? "text-xl text-purple-900" : "text-base"}`}>
        <Users className={isGroup ? "w-6 h-6 text-purple-700" : "w-4 h-4"} />
        {isGroup ? "🎉 GROUP BOOKING" : "Passengers"} ({passengers.length})
      </h4>
      <div className="space-y-2">
        {passengers.map(function(passenger, idx) {
          return (
            <div key={idx} className={`flex items-center justify-between p-3 rounded-lg border-2 ${isGroup ? "bg-white border-purple-300 shadow-sm" : "bg-gray-50 border-transparent"}`}>
              <div className="flex items-center gap-3">
                <div className={`${isGroup ? "w-12 h-12 text-lg" : "w-8 h-8 text-sm"} bg-gradient-to-br from-purple-200 to-pink-200 rounded-full flex items-center justify-center text-purple-900 font-bold flex-shrink-0`}>
                  {idx + 1}
                </div>
                <div>
                  <p className={`font-bold ${isGroup ? "text-lg text-purple-950" : "text-sm"}`}>{passenger.name}</p>
                  <p className={`${isGroup ? "text-sm" : "text-xs"} text-green-700 font-semibold`}>{passenger.whatsapp}</p>
                </div>
              </div>
              {idx === 0 && (
                <Badge className={isGroup ? "bg-amber-400 text-amber-950 text-xs font-bold border-amber-500" : "text-xs"} variant={isGroup ? "default" : "outline"}>
                  Main Contact
                </Badge>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DriverSection({ hasDriver, booking, drivers, onUnassign, onAssign }) {
  return (
    <div className="border-t pt-4">
      <h4 className="font-semibold mb-3 flex items-center gap-2">
        <Car className="w-4 h-4" />
        Assigned Driver
      </h4>
      {hasDriver ? (
        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
              {booking.assigned_driver_name.charAt(0)}
            </div>
            <div>
              <p className="font-semibold">{booking.assigned_driver_name}</p>
              <p className="text-xs text-green-600">{booking.assigned_driver_phone}</p>
            </div>
          </div>
          <ConfirmAction
            title="Remove Driver?"
            description={`Remove ${booking.assigned_driver_name} from this booking?`}
            onConfirm={onUnassign}
            confirmText="Yes, Remove"
            variant="destructive"
          >
            <Button variant="outline" size="sm" className="text-red-600" data-testid="unassign-driver-btn">
              Remove
            </Button>
          </ConfirmAction>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">No driver assigned yet</p>
          <div className="flex flex-wrap gap-2">
            {drivers.map(function(driver) {
              return (
                <ConfirmAction
                  key={driver.id}
                  title="Assign Driver?"
                  description={`Assign ${driver.name} to this booking?`}
                  onConfirm={function() { onAssign(driver.id, driver.name); }}
                  confirmText="Yes, Assign"
                >
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                    data-testid={`assign-driver-${driver.id}`}
                  >
                    <UserCheck className="w-4 h-4" />
                    {driver.name}
                  </Button>
                </ConfirmAction>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
